import { TableToolbar, SortableHeader } from '../components/TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../lib/tableControls'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardX,
  Bot,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react'

import { supabase, signOut } from '../lib/supabase'
import { cn } from '../lib/utils'
import { useAuth, useTeamAccess } from '../contexts/AuthContext'
import {
  toDateString,
  previousDateString,
  zonedTimeToUtc,
} from '../lib/timezone'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { DatePicker } from '../components/ui/date-picker'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

// ============================================================================
// Page: /dashboard/team/:teamId/standups/check-ins
//
// "Check-ins" — wired to real Supabase data (previously a static mockup, see
// git history for that version). Data model, per the planning pass:
//
// - Person / avatar / "owned by": agents.user_id -> users.id, same join
//   StandupUserCards.tsx already uses.
// - Update text: standup_responses.questions->>'today' for humans,
//   agent_checkin.task for agents.
// - Team column: pods.name via users.pod_id — pods are a brand-new concept
//   (see 20260905180000_add_pods.sql), seeded but with no admin UI yet.
//   Agents don't have their own pod_id; their pod is derived through their
//   owner (agents.user_id -> users.pod_id), same as "owned by".
// - Status (On time / Missed): "On time" = submitted before the team's
//   standup_config.digest_time (converted from the team's timezone via
//   src/lib/timezone.ts — no tz library in this project); "Missed" covers
//   both a late submission and no submission at all, shown as the same
//   badge. Explicit product decision, 2026-09-05: digest_time (not dm_time)
//   is the real deadline, since it's when the compiled digest posts.
// - Stat cards + day-over-day deltas: computed client-side from today's and
//   yesterday's rows (two dates fetched in one query per table).
// - "Missed today" / "Checked in today" count presence only (no lateness) —
//   deliberately different from the per-row Status badge, which folds in
//   lateness. Matches the original mock's arithmetic (checked-in + missed =
//   total members).
//
// agent_checkin has no team_id column (checked the schema) — scoped here by
// first resolving the team's agent ids via agents_team_visible, then
// filtering agent_checkin by agent_id, rather than fetching every team's
// check-ins and relying only on the client-side join like
// StandupUserCards.tsx currently does.
//
// 2026-09-29: the agent-checkin fetch below was repointed from a raw
// `.from('agent_checkin')` select to the get_team_agent_checkin_aggregates
// RPC (see 20260929140000_add_team_agent_checkin_aggregates_rpc.sql and the
// identical note in TopModelsUsed.tsx). agent_checkin's SELECT RLS went
// admin/manager-only on 2026-09-28, which silently zeroed the "Agent
// check-ins" stat card for plain members. Only agent_id/checkin_date are
// actually used here (a count, via checkinsByAgentId.size below) -- `task`
// (the agent's actual check-in text) is fetched but was already unused
// by this page, so dropping it for the aggregate/no-owner-identity RPC
// costs nothing real.
// ============================================================================

interface TeamCheckinsProps {
  teamId: string
}

interface Member {
  id: string
  name: string | null
  email: string | null
  avatar_url: string | null
  pod_id: string | null
}

interface Pod {
  id: string
  name: string
}

interface AgentInfo {
  id: string
  user_id: string
  name: string
  client_id: string | null
  client_name: string | null
  paused_at: string | null
}

interface StandupRow {
  user_id: string | null
  questions: { yesterday?: string; today?: string; blockers?: string } | null
  created_at: string
  date: string
}

// Shape matches get_team_agent_checkin_aggregates' RETURNS TABLE (see
// 20260929140000_add_team_agent_checkin_aggregates_rpc.sql) -- narrower than
// agent_checkin itself since only agent_id/checkin_date are actually used
// below (a per-agent-per-day count). `task`/`created_at` used to be fetched
// here too but were already unused by this page.
interface CheckinRow {
  agent_id: string
  checkin_date: string
}

interface StandupConfig {
  enabled?: boolean
  digest_enabled?: boolean
  timezone?: string
  digest_time?: string
  dm_time?: string
  days_of_week?: number[]
}

const DEFAULT_TIMEZONE = 'UTC'
const DEFAULT_DIGEST_TIME = '17:00'
const PAGE_SIZE = 8

const AVATAR_PALETTE = [
  'bg-sky-100 text-sky-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-cyan-100 text-cyan-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
]

function paletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

function initialsOf(name: string): string {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return initials || '?'
}

function displayName(member: Pick<Member, 'name' | 'email'>): string {
  return member.name || member.email?.split('@')[0] || 'Unknown user'
}

type RowStatus = 'on-time' | 'missed'
type AttentionFilter = 'all' | 'needs-attention' | 'missed' | 'agent-only'

interface CheckinTableRow {
  id: string
  userId: string
  kind: 'human' | 'agent'
  name: string
  avatarUrl?: string | null
  podId: string | null
  podName: string
  submittedAt: Date | null
  update: string | null
  agentActivityCount: number
  status: RowStatus
}

type TrendDirection = 'up' | 'down' | 'flat'

interface StatCard {
  label: string
  icon: React.ReactNode
  value: string
  trendLabel: string
  trendDirection: TrendDirection
  /** Whether an "up" trend is good news for this metric (green) or bad (red). */
  upIsGood: boolean
}

function TrendIndicator({ direction, upIsGood }: { direction: TrendDirection; upIsGood: boolean }) {
  const isGood = direction === 'flat' ? true : direction === 'up' ? upIsGood : !upIsGood
  const colorClass = isGood ? 'text-success' : 'text-destructive'
  const Icon = direction === 'down' ? ArrowDown : direction === 'up' ? ArrowUp : Minus
  return <Icon className={`h-3 w-3 ${colorClass}`} />
}

function countDelta(today: number, yesterday: number): { direction: TrendDirection; label: string } {
  const delta = today - yesterday
  if (delta === 0) return { direction: 'flat', label: 'Same as yesterday' }
  const word = delta > 0 ? 'more' : 'fewer'
  return { direction: delta > 0 ? 'up' : 'down', label: `${Math.abs(delta)} ${word} than yesterday` }
}

const TeamCheckins: React.FC<TeamCheckinsProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const { role, loading: roleLoading } = useTeamAccess(teamId)
  const { user } = useAuth()
  const isManager = role === 'manager'
  const isMember = role === 'member'
  const canManageSchedule = role === 'owner' || role === 'admin'
  // Read an initial ?pod= param (set by the Summary page's "View all
  // check-ins" links) to preselect the pod filter on arrival. Only consulted
  // once, on mount — after that the Select below is the source of truth.
  const [searchParams] = useSearchParams()
  const hadExplicitPodParam = !!searchParams.get('pod')
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [config, setConfig] = useState<StandupConfig>({})
  const [selectedDate, setSelectedDate] = useState<string>(searchParams.get('date') || toDateString(new Date()))
  const [selectedPodId, setSelectedPodId] = useState<string>(searchParams.get('pod') || 'all')
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>(searchParams.get('filter') === 'missed' ? 'missed' : 'all')
  const [page, setPage] = useState(0)
  const [rowSearch, setRowSearch] = useState('')
  const { sort, toggleSort } = useTableSort('submitted')
  // RLS is authoritative: members see themselves, managers see assigned
  // teams, and owners/admins see the workspace. Filters only narrow that scope.
  const [managedPodIds, setManagedPodIds] = useState<Set<string>>(new Set())
  const [appliedManagerDefault, setAppliedManagerDefault] = useState(false)

  const [members, setMembers] = useState<Member[]>([])
  const [pods, setPods] = useState<Pod[]>([])
  const [agents, setAgents] = useState<AgentInfo[]>([])
  const [standupsToday, setStandupsToday] = useState<StandupRow[]>([])
  const [standupsYesterday, setStandupsYesterday] = useState<StandupRow[]>([])
  const [checkinsToday, setCheckinsToday] = useState<CheckinRow[]>([])
  const [checkinsYesterday, setCheckinsYesterday] = useState<CheckinRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const scopePods = useMemo(() => isManager ? pods.filter(pod => managedPodIds.has(pod.id)) : pods, [isManager, pods, managedPodIds])
  // A hidden/stale team filter must never leave a member stuck on an empty view.
  const scopePodId = isMember || !scopePods.some(pod => pod.id === selectedPodId) ? 'all' : selectedPodId
  const hasScheduledStandups = config.enabled === true || config.digest_enabled === true
  const reminderDays = [...new Set((Array.isArray(config.days_of_week) ? config.days_of_week : []).filter(day => Number.isInteger(day) && day >= 0 && day <= 6))]
    .sort((a, b) => a - b).map(day => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]).join(', ')
  const reminderTime = typeof config.dm_time === 'string' && /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(config.dm_time) ? config.dm_time.slice(0, 5) : null

  const previousDate = previousDateString(selectedDate)

  useEffect(() => {
    setPage(0)
  }, [selectedDate, scopePodId, attentionFilter, rowSearch, sort])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const [teamResult, membersResult, podsResult, agentsResult] = await Promise.all([
        supabase.from('teams').select('name, standup_config').eq('id', teamId).single(),
        // pod_id postdates the last generation of src/types/supabase.ts (see
        // 20260905180000_add_pods.sql) — cast like agents_team_visible/
        // agent_checkin below, same reason.
        (supabase.from as any)('users')
          .select('id, name, email, avatar_url, pod_id')
          .eq('team_id', teamId),
        (supabase.from as any)('pods').select('id, name').eq('team_id', teamId),
        (supabase.from as any)('agents_team_visible')
          .select('id, user_id, name, client_id, client_name, paused_at')
          .eq('team_id', teamId),
      ])

      if (cancelled) return

      if (teamResult.error || membersResult.error || podsResult.error || agentsResult.error) {
        console.error(
          'Failed to load Check-ins page data:',
          teamResult.error ?? membersResult.error ?? podsResult.error ?? agentsResult.error
        )
        setError('Could not load check-ins for this team.')
        setLoading(false)
        return
      }

      const agentRows = (agentsResult.data ?? []) as AgentInfo[]
      const agentIds = agentRows.map((a) => a.id)

      const [standupsResult, checkinsResult] = await Promise.all([
        supabase
          .from('standup_responses')
          .select('user_id, questions, created_at, date')
          .eq('team_id', teamId)
          .in('date', [selectedDate, previousDate]),
        agentIds.length > 0
          ? (supabase.rpc as any)('get_team_agent_checkin_aggregates', {
              p_team_id: teamId,
              p_from: previousDate,
              p_to: selectedDate,
            })
          : Promise.resolve({ data: [] as CheckinRow[], error: null }),
      ])

      if (cancelled) return

      if (standupsResult.error || checkinsResult.error) {
        console.error(
          'Failed to load Check-ins page activity:',
          standupsResult.error ?? checkinsResult.error
        )
        setError('Could not load check-ins for this team.')
        setLoading(false)
        return
      }

      const allStandups = (standupsResult.data ?? []) as unknown as StandupRow[]
      const allCheckins = (checkinsResult.data ?? []) as CheckinRow[]

      setTeamName(teamResult.data?.name ?? undefined)
      setConfig((teamResult.data?.standup_config as StandupConfig | null) ?? {})
      setMembers((membersResult.data ?? []) as Member[])
      setPods((podsResult.data ?? []) as Pod[])
      setAgents(agentRows)
      setStandupsToday(allStandups.filter((s) => s.date === selectedDate))
      setStandupsYesterday(allStandups.filter((s) => s.date === previousDate))
      setCheckinsToday(allCheckins.filter((c) => c.checkin_date === selectedDate))
      setCheckinsYesterday(allCheckins.filter((c) => c.checkin_date === previousDate))
      setLoading(false)
    }

    if (!roleLoading) load()
    else setLoading(true)
    return () => {
      cancelled = true
    }
  }, [teamId, selectedDate, previousDate, role, roleLoading])

  // Managed pods, manager-only -- same query pattern as TeamAgents.tsx /
  // PodStandupOverrides.tsx (pod_managers' own SELECT policy is
  // permissive/true, not team-scoped, so this filters to this team's pod
  // ids itself).
  useEffect(() => {
    let cancelled = false

    const loadManagedPods = async () => {
      if (!isManager || !user?.id || pods.length === 0) {
        setManagedPodIds(new Set())
        return
      }
      const { data, error: pmError } = await (supabase.from as any)('pod_managers')
        .select('pod_id')
        .eq('auth_user_id', user.id)
        .in(
          'pod_id',
          pods.map((p) => p.id)
        )
      if (cancelled) return
      if (pmError) {
        console.error('Failed to load managed pods:', pmError)
        return
      }
      setManagedPodIds(new Set((data ?? []).map((r: { pod_id: string }) => r.pod_id)))
    }

    loadManagedPods()
    return () => {
      cancelled = true
    }
  }, [isManager, user?.id, pods])

  // Apply the manager default exactly once, and only when nothing else has
  // already decided the filter (no ?pod= param, still sitting on "all").
  // Multiple managed pods -> leave all managed teams selected rather than guessing
  // which one they want first.
  useEffect(() => {
    if (appliedManagerDefault || hadExplicitPodParam) return
    if (!isManager || managedPodIds.size !== 1 || selectedPodId !== 'all') return
    setSelectedPodId([...managedPodIds][0])
    setAppliedManagerDefault(true)
  }, [appliedManagerDefault, hadExplicitPodParam, isManager, managedPodIds, selectedPodId])

  const timezone = config.timezone || DEFAULT_TIMEZONE
  const digestTime = config.digest_time || DEFAULT_DIGEST_TIME

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const { statCards, rows, totalPages, pageRows } = useMemo(() => {
    const podsById = new Map(pods.map((p) => [p.id, p.name]))
    const membersById = new Map(members.map((m) => [m.id, m]))

    const standupByUserId = new Map<string, StandupRow>()
    for (const s of standupsToday) if (s.user_id) standupByUserId.set(s.user_id, s)
    const standupByUserIdYesterday = new Map<string, StandupRow>()
    for (const s of standupsYesterday) if (s.user_id) standupByUserIdYesterday.set(s.user_id, s)

    const checkinsByAgentId = new Map<string, CheckinRow[]>()
    for (const c of checkinsToday) {
      const list = checkinsByAgentId.get(c.agent_id)
      if (list) list.push(c)
      else checkinsByAgentId.set(c.agent_id, [c])
    }
    const checkinsByAgentIdYesterday = new Map<string, CheckinRow[]>()
    for (const c of checkinsYesterday) {
      const list = checkinsByAgentIdYesterday.get(c.agent_id)
      if (list) list.push(c)
      else checkinsByAgentIdYesterday.set(c.agent_id, [c])
    }

    const agentIdsByUserId = new Map<string, string[]>()
    for (const agent of agents) {
      const owned = agentIdsByUserId.get(agent.user_id) ?? []
      owned.push(agent.id)
      agentIdsByUserId.set(agent.user_id, owned)
    }

    const cutoffToday = zonedTimeToUtc(selectedDate, digestTime, timezone)

    const statusOf = (submittedAt: Date | null): RowStatus => {
      if (!submittedAt) return 'missed'
      return submittedAt.getTime() <= cutoffToday.getTime() ? 'on-time' : 'missed'
    }

    const humanRows: CheckinTableRow[] = members.map((member) => {
      const standup = standupByUserId.get(member.id) ?? null
      const submittedAt = standup ? new Date(standup.created_at) : null
      const agentActivityCount = (agentIdsByUserId.get(member.id) ?? []).reduce(
        (total, agentId) => total + (checkinsByAgentId.get(agentId)?.length ?? 0),
        0
      )
      return {
        id: `human-${member.id}`,
        userId: member.id,
        kind: 'human',
        name: displayName(member),
        avatarUrl: member.avatar_url,
        podId: member.pod_id,
        podName: member.pod_id ? podsById.get(member.pod_id) ?? '—' : '—',
        submittedAt,
        update: standup?.questions?.today ?? null,
        agentActivityCount,
        status: statusOf(submittedAt),
      }
    })

    // "Today's check-ins" is human standups only — agent activity is
    // reported solely through the "Agent check-ins" stat card above (see
    // checkinsByAgentId/checkinsByAgentIdYesterday below), not as table
    // rows. Explicit product decision, 2026-09-05: the two were previously
    // combined here, which mixed a human roll-call with agent task activity
    // in a single list.
    const allRows = [...humanRows].sort((a, b) => {
      if (a.submittedAt && b.submittedAt) return a.submittedAt.getTime() - b.submittedAt.getTime()
      if (a.submittedAt) return -1
      if (b.submittedAt) return 1
      return a.name.localeCompare(b.name)
    })

    const teamFilteredRows =
      scopePodId === 'all' ? allRows : allRows.filter((r) => r.podId === scopePodId)
    const filteredRows = teamFilteredRows.filter((row) => {
      if (attentionFilter === 'all') return true
      if (attentionFilter === 'missed') return row.status === 'missed'
      if (attentionFilter === 'agent-only') return !row.submittedAt && row.agentActivityCount > 0
      return row.status === 'missed'
    })

    // ---- stat cards (scoped to the selected pod -- previously these always
    //      summed the whole team even when a specific pod was chosen, which
    //      silently disagreed with the pod-filtered table underneath them,
    //      same bug class already fixed on the Summary page) ----
    const scopedMembers =
      scopePodId === 'all' ? members : members.filter((m) => m.pod_id === scopePodId)
    // Paused agents (2026-10-03) are excluded from this stat entirely, not
    // just from the numerator -- an agent its owner intentionally paused
    // isn't "missing" a check-in, it's correctly not checking in, so leaving
    // it in the denominator would make "Agent check-ins X / Y" read as a
    // permanent, worsening shortfall for something that isn't a problem.
    const activeAgents = agents.filter((a) => !a.paused_at)
    const scopedAgents =
      scopePodId === 'all'
        ? activeAgents
        : activeAgents.filter((a) => membersById.get(a.user_id)?.pod_id === scopePodId)
    const scopedMemberIds = new Set(scopedMembers.map((m) => m.id))

    const checkedInToday = scopedMembers.filter((m) => standupByUserId.has(m.id)).length
    const checkedInYesterday = scopedMembers.filter((m) => standupByUserIdYesterday.has(m.id)).length
    const totalMembers = scopedMembers.length
    const missedToday = Math.max(totalMembers - checkedInToday, 0)
    const missedYesterday = Math.max(totalMembers - checkedInYesterday, 0)

    const agentCheckedInToday = scopedAgents.filter((a) => checkinsByAgentId.has(a.id)).length
    const agentCheckedInYesterday = scopedAgents.filter((a) => checkinsByAgentIdYesterday.has(a.id)).length
    const totalAgents = scopedAgents.length

    const checkedInDelta = countDelta(checkedInToday, checkedInYesterday)
    const missedDelta = countDelta(missedToday, missedYesterday)
    const agentDelta = countDelta(agentCheckedInToday, agentCheckedInYesterday)

    const cards: StatCard[] = [
      {
        label: 'Checked in today',
        icon: <ClipboardCheck className="h-4 w-4" />,
        value: `${checkedInToday} / ${totalMembers}`,
        trendLabel: checkedInDelta.label,
        trendDirection: checkedInDelta.direction,
        upIsGood: true,
      },
      {
        label: 'Missed today',
        icon: <ClipboardX className="h-4 w-4" />,
        value: `${missedToday}`,
        trendLabel: missedDelta.label,
        trendDirection: missedDelta.direction,
        upIsGood: false,
      },
      {
        label: 'Connector activity',
        icon: <Bot className="h-4 w-4" />,
        value: `${agentCheckedInToday} / ${totalAgents}`,
        trendLabel: agentDelta.label,
        trendDirection: agentDelta.direction,
        upIsGood: true,
      },
    ]

    const searchedRows = sortTableRows(filteredRows.filter(row => matchesTableSearch(rowSearch, row.name, row.podName, row.update)), sort, {
      name: row => row.name, team: row => row.podName === '—' ? null : row.podName, submitted: row => row.submittedAt?.getTime(),
      update: row => row.update, activity: row => row.agentActivityCount, status: row => row.status,
    })
    const totalPagesOut = Math.max(Math.ceil(searchedRows.length / PAGE_SIZE), 1)
    const safePage = Math.min(page, totalPagesOut - 1)
    const pageRowsOut = searchedRows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

    return { statCards: cards, rows: searchedRows, totalPages: totalPagesOut, pageRows: pageRowsOut }
  }, [
    members,
    pods,
    agents,
    standupsToday,
    standupsYesterday,
    checkinsToday,
    checkinsYesterday,
    selectedDate,
    scopePodId,
    attentionFilter,
    rowSearch, sort,
    timezone,
    digestTime,
    page,
  ])

  const selectedDateObj = new Date(selectedDate + 'T00:00:00')

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="standups-checkins"
        onNavigate={goToDashboard}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId}
      />
      <SidebarInset className="min-w-0">
        <TopBar
          teamId={teamId}
          teamName={teamName}
          onLogout={handleLogout}
          sectionLabel="Standups"
        />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-1">
                {roleLoading || !role ? 'Standups' : isMember ? 'Your standups' : isManager ? 'Managed team standups' : 'Team Standups'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {isMember ? 'Your updates and connector activity.' : isManager ? 'Standups for the teams you manage.' : 'Team updates and connector activity.'}
              </p>
            </div>
            {!loading && !roleLoading && !error && canManageSchedule && (
              <Button asChild className="h-auto min-h-11 shrink-0 py-2">
                <Link to={`/dashboard/team/${teamId}/config?section=schedule`}>{hasScheduledStandups ? 'Manage schedule' : 'Set up standups'}</Link>
              </Button>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!loading && !roleLoading && !error && config.enabled !== true && config.digest_enabled !== true && (
            <Alert role="status">
              <AlertTitle>Standup reminders are optional</AlertTitle>
              <AlertDescription>
                {canManageSchedule
                  ? 'Choose your schedule and Slack channel when you want to use reminders. They stay off until you enable and save them.'
                  : 'Your workspace has not enabled scheduled standups. An owner or admin can set them up.'}
              </AlertDescription>
            </Alert>
          )}

          {!loading && !roleLoading && !error && hasScheduledStandups && (
            <div className="space-y-1 border border-border bg-muted/30 p-4 text-sm" aria-label="Standup schedule">
              <h2 className="font-medium">Standup schedule</h2>
              <p className="text-muted-foreground">
                {config.enabled !== true ? 'Slack reminders are off.' : reminderTime && reminderDays
                  ? `Slack reminders: ${reminderTime}, ${reminderDays}, ${config.timezone || DEFAULT_TIMEZONE}.`
                  : 'The reminder schedule is incomplete.'}
                {config.digest_enabled === true && ' Daily summaries are enabled.'}
              </p>
              {!canManageSchedule && <p className="text-muted-foreground">An owner or admin can change the shared reminder time. Individual reminder times are not currently available.</p>}
            </div>
          )}

          {!error && (
            // One seamless bordered block (stat strip + check-ins panel + pagination),
            // matching TeamManagement.tsx's KPI strip and table treatment
            // (docs/UI_STYLE_GUIDE.md §2, §4, §6) instead of separate floating Cards.
            <div className="divide-y divide-border overflow-hidden border border-border bg-background shadow-sm">
              <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-3">
                {statCards.map((stat) => (
                  <div key={stat.label} className="space-y-1 p-4 sm:p-5">
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {stat.icon}
                      <span>{stat.label}</span>
                    </div>
                    <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                      {loading ? '—' : stat.value}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <TrendIndicator direction={stat.trendDirection} upIsGood={stat.upIsGood} />
                      <span
                        className={
                          stat.trendDirection === 'flat' ||
                          (stat.trendDirection === 'up') === stat.upIsGood
                            ? 'font-medium text-success'
                            : 'font-medium text-destructive'
                        }
                      >
                        {loading ? ' ' : stat.trendLabel}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 p-4 sm:p-6">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Today&apos;s standups</h2>
                    <p className="text-sm text-muted-foreground">
                      {isMember ? 'Your standup status with your connector activity for context.' : 'Standup status with connector activity for context.'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <Select
                      value={attentionFilter}
                      onValueChange={(value) => setAttentionFilter(value as AttentionFilter)}
                    >
                      <SelectTrigger aria-label="Filter standups" className="w-full sm:w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="needs-attention">Needs attention</SelectItem>
                        <SelectItem value="missed">Missed standups</SelectItem>
                      <SelectItem value="agent-only">Connector activity, no standup</SelectItem>
                      </SelectContent>
                    </Select>
                    {!roleLoading && !isMember && (canManageSchedule || isManager) && (scopePods.length > 1 || scopePodId !== 'all') && <Select value={scopePodId} onValueChange={setSelectedPodId}>
                      <SelectTrigger aria-label="Filter teams" className="w-full sm:w-40">
                        <SelectValue placeholder={isManager ? 'Managed teams' : 'All teams'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isManager ? 'Managed teams' : 'All teams'}</SelectItem>
                        {scopePods.map((pod) => (
                          <SelectItem key={pod.id} value={pod.id}>
                            {pod.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>}
                    <DatePicker
                      date={selectedDateObj}
                      onDateChange={(date) => date && setSelectedDate(toDateString(date))}
                      className="w-full sm:w-44 bg-card border border-border"
                      placeholder="Select date"
                    />
                  </div>
                </div>

                <TableToolbar label="standups" query={rowSearch} onQueryChange={setRowSearch} count={rows.length} total={members.length}
                  active={!!rowSearch || attentionFilter !== 'all' || scopePodId !== 'all'} onClear={() => { setRowSearch(''); setAttentionFilter('all'); setSelectedPodId('all') }} />

                {attentionFilter === 'missed' && !loading && rows.length > 0 && (
                  <div className="flex items-center gap-2 border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                    <ClipboardX className="h-4 w-4 shrink-0" />
                    <span><strong>{rows.length}</strong> missed standup{rows.length === 1 ? '' : 's'} on {selectedDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}. These rows need follow-up.</span>
                  </div>
                )}

                {loading ? (
                  // Shape mirrors a loaded check-in row (circular person avatar,
                  // name, team/submitted/update text, status pill) so the
                  // shimmer reads as "rows incoming" rather than a generic bar.
                  <div className="-mx-4 overflow-x-auto sm:-mx-6">
                    <table className="w-full min-w-[820px] table-fixed caption-bottom text-sm">
                      <tbody>
                        {Array.from({ length: PAGE_SIZE }, (_, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-4 align-middle">
                              <div className="flex items-center gap-3">
                                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                                <Skeleton className="h-4 w-28" />
                              </div>
                            </td>
                            <td className="p-4 align-middle">
                              <Skeleton className="h-4 w-16" />
                            </td>
                            <td className="p-4 align-middle">
                              <Skeleton className="h-4 w-16" />
                            </td>
                            <td className="p-4 align-middle">
                              <Skeleton className="h-4 w-44" />
                            </td>
                            <td className="p-4 align-middle">
                              <Skeleton className="h-6 w-20 rounded-full" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : rows.length === 0 ? (
                  // Matches TeamManagement.tsx's own empty-member-list treatment
                  // (icon + bold line + description), not the dashboard cards'
                  // compact EmptyState -- this page follows Team Management.
                  <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                    <ClipboardCheck className="h-8 w-8 text-muted-foreground" />
                    <p className="font-medium text-foreground">
                      {attentionFilter === 'all' && !rowSearch ? 'No standups yet' : 'No people match this filter'}
                    </p>
                    <p className="max-w-sm text-sm text-muted-foreground">
                      {attentionFilter === 'all' && !rowSearch
                        ? 'No one on this team has checked in for this day.'
                        : 'Try a different filter or date.'}
                    </p>
                  </div>
                ) : (
                  // Full-bleed out of this panel's own p-4/sm:p-6 so row dividers
                  // reach the panel's true edges (§4), same treatment as
                  // TeamManagement.tsx's member table.
                  <div className="-mx-4 overflow-x-auto sm:-mx-6">
                    <table className="w-full min-w-[820px] caption-bottom text-sm">
                      <thead className="[&_tr]:border-b">
                        <tr className="border-b">
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="name" label="Person Name" sort={sort} onSort={toggleSort} />
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="team" label="Team" sort={sort} onSort={toggleSort} />
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="submitted" label="Submitted" sort={sort} onSort={toggleSort} />
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="update" label="Update" sort={sort} onSort={toggleSort} />
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="activity" label="Connector activity" sort={sort} onSort={toggleSort} />
                          <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="status" label="Status" sort={sort} onSort={toggleSort} />
                          <th className="h-11 w-12 px-4 text-right align-middle font-medium text-muted-foreground" aria-label="Details" />
                        </tr>
                      </thead>
                      {/* No :last-child border-0 exception, per §3 -- every row,
                          including the last, keeps its own divider. */}
                      <tbody>
                        {pageRows.map((row) => (
                          <tr
                            key={row.id}
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/dashboard/team/${teamId}/standups/${row.userId}`)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                navigate(`/dashboard/team/${teamId}/standups/${row.userId}`)
                              }
                            }}
                            aria-label={`View ${row.name}'s standup and connector activity`}
                            className={cn(
                              'cursor-pointer border-b outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                              attentionFilter === 'missed' && 'bg-rose-50/45 hover:bg-rose-50',
                            )}
                          >
                            <td className="p-4 align-middle">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt="" />}
                                  <AvatarFallback
                                    className={`text-xs font-semibold ${paletteFor(row.id)}`}
                                  >
                                    {initialsOf(row.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-medium text-foreground">{row.name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 align-middle text-muted-foreground">{row.podName}</td>
                            <td className="p-4 align-middle text-muted-foreground">
                              {row.submittedAt
                                ? row.submittedAt.toLocaleTimeString('en-US', {
                                    timeZone: timezone,
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    timeZoneName: 'short',
                                  })
                                : '—'}
                            </td>
                            <td className="max-w-0 p-4 align-middle text-foreground">
                              <p className="truncate" title={row.update || 'No update yet'}>
                                {row.update || 'No update yet'}
                              </p>
                            </td>
                            <td className="p-4 align-middle text-muted-foreground">
                              {row.agentActivityCount > 0
                                ? `${row.agentActivityCount} ${row.agentActivityCount === 1 ? 'update' : 'updates'}${!row.submittedAt ? ' · no standup' : ''}`
                                : 'No activity'}
                            </td>
                            <td className="p-4 align-middle">
                              {/* Pill + dot, per UI_STYLE_GUIDE §8 -- NOT the shadcn
                                  Badge success/destructive variants: this app's
                                  --success token renders amber, not the green this
                                  status needs (see index.css and TeamManagement.tsx's
                                  own comment, which names this exact badge as the
                                  counter-example). Raw emerald/rose swatches instead. */}
                              {row.status === 'on-time' ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                  On time
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                                  Missed
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-right align-middle">
                              <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination is a sibling of the content block above (not nested in
                  its padding), pinned to this panel's own bottom edge -- plain icon
                  buttons, [prev][Page X of Y][next], per §6 / TeamManagement.tsx. */}
              {!loading && rows.length > 0 && (
                <div className="flex items-stretch divide-x divide-border border-t border-border">
                  <div className="flex-1" />
                  <button
                    type="button"
                    aria-label="Previous page"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(p - 1, 0))}
                    className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
                  >
                    <ChevronLeft
                      className={cn('h-5 w-5', page === 0 ? 'text-muted-foreground/40' : 'text-foreground')}
                    />
                  </button>
                  <div className="flex w-40 items-center justify-center py-[18px] text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </div>
                  <button
                    type="button"
                    aria-label="Next page"
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                    className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
                  >
                    <ChevronRight
                      className={cn(
                        'h-5 w-5',
                        page + 1 >= totalPages ? 'text-muted-foreground/40' : 'text-foreground'
                      )}
                      strokeWidth={2.5}
                    />
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default TeamCheckins
